import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, FileText, Users } from 'lucide-react';
import { GroupContractType } from 'api/contract';
import { ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { Dropdown, DropdownItem } from 'components/forms/Dropdown';
import Loading from 'components/ui/Loader';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { GroupContractStepIdentifier } from '../types/enums';
import { ADMIN_PROCESS_PREFIX, BROKER_PROCESS_PREFIX, buildProcessRoute } from '../utils/getRouteByStep';
import { createGroupContract } from '../api/groupContractSubscribersApi';

interface NewGroupContractTypeModalProps {
  onClose: () => void;
  /**
   * "broker" mode: plateforme is locked (resolved server-side).
   * "admin" mode: plateforme is explicitly chosen.
   */
  mode?: 'admin' | 'broker';
}

/**
 * type → (admin) plateforme → creating.
 * Pas d'étape de portée : elle est déduite côté serveur
 * (courtier → PersonneMorale, compagnie/admin → Plateforme).
 */
type Step = 'type' | 'plateforme' | 'creating';

type CardVisual = {
  icon: JSX.Element;
  gradient: string;
  bgColor: string;
  hoverBg: string;
};

const getTypeVisual = (type: GroupContractType): CardVisual => {
  switch (type) {
    case GroupContractType.Group:
      return {
        icon: <Users className="w-8 h-8" />,
        gradient: 'from-blue-500 to-cyan-500',
        bgColor: 'bg-blue-50',
        hoverBg: 'hover:bg-blue-100',
      };
    case GroupContractType.Cadre:
      return {
        icon: <FileText className="w-8 h-8" />,
        gradient: 'from-purple-500 to-indigo-500',
        bgColor: 'bg-purple-50',
        hoverBg: 'hover:bg-purple-100',
      };
    default:
      return {
        icon: <Users className="w-8 h-8" />,
        gradient: 'from-gray-500 to-slate-500',
        bgColor: 'bg-gray-50',
        hoverBg: 'hover:bg-gray-100',
      };
  }
};

export const NewGroupContractTypeModal = ({
  onClose,
  mode = 'admin',
}: NewGroupContractTypeModalProps) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  const navigate = useNavigate();
  const plateformeData = useAppSelector(selectReferentialDataByKey(ReferentialKey.Plateforme)) || [];

  const [step, setStep] = useState<Step>('type');
  const [type, setType] = useState<GroupContractType | null>(null);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [plateformeId, setPlateformeId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plateformeItems: DropdownItem[] = plateformeData.map(r => ({
    key: r.id,
    label: r.label,
  }));

  const typeOptions: { type: GroupContractType; titleKey: string; descKey: string }[] = [
    {
      type: GroupContractType.Group,
      titleKey: 'GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.GROUP_TITLE',
      descKey: 'GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.GROUP_DESCRIPTION',
    },
    {
      type: GroupContractType.Cadre,
      titleKey: 'GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.MASTER_TITLE',
      descKey: 'GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.MASTER_DESCRIPTION',
    },
  ];

  const createContract = async (selectedType: GroupContractType) => {
    setStep('creating');
    setIsSubmitting(true);
    setError(null);
    try {
      // Le mode admin/courtier/compagnie est arbitré côté back par le RÔLE ACTIF
      // (switch de rôle) — plus de flag isAdminContext envoyé par le client.
      const response = await createGroupContract({
        type: selectedType,
        ...(mode === 'admin' ? { plateformeId } : {}),
      });
      if (!response.id) {
        throw new Error(
          response.errorMessage
          || t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.ERROR_NO_ID'),
        );
      }
      onClose();
      const prefix = mode === 'broker' ? BROKER_PROCESS_PREFIX : ADMIN_PROCESS_PREFIX;
      navigate(buildProcessRoute(response.id, GroupContractStepIdentifier.Subscriber, prefix));
    } catch (err: any) {
      setError(err?.message || t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.ERROR_DEFAULT'));
      setStep('type');
      setType(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTypeSelect = async (selected: GroupContractType) => {
    setError(null);
    setType(selected);

    if (mode === 'admin') {
      // Admin : il ne reste qu'à choisir l'OU, la portée est résolue côté back.
      setStep('plateforme');
      return;
    }

    // Mode espace pro : création directe — la portée est déduite du contexte de
    // connexion (courtier → PersonneMorale, compagnie → Plateforme).
    await createContract(selected);
  };

  const handleBackToTypes = () => {
    setStep('type');
    setType(null);
    setPlateformeId('');
    setError(null);
  };

  const handleCreateForPlateforme = async () => {
    if (type === null || plateformeId === '') return;
    await createContract(type);
  };

  // -------- ÉTAPE 1 : sélection du type --------
  if (step === 'type') {
    return (
      <div className="max-w-6xl mx-auto p-10">
        <div className="text-center mt-2 mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.TITLE')}
          </h2>
          <p className="text-gray-600 text-lg">
            {t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.DESCRIPTION')}
          </p>
        </div>

        {error && (
          <div className="max-w-md mx-auto mb-6 text-center text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {typeOptions.map(option => {
            const { icon, gradient, bgColor, hoverBg } = getTypeVisual(option.type);
            const isHovered = hoveredCard === `type-${option.type}`;

            return (
              <div
                key={option.type}
                className={`group relative cursor-pointer rounded-2xl border-2 border-gray-100 ${bgColor} ${hoverBg}
                         transition-all duration-300 ease-in-out transform hover:scale-[1.02] hover:shadow-xl
                         ${isHovered ? 'shadow-xl scale-[1.02]' : 'shadow-lg'}`}
                onClick={() => handleTypeSelect(option.type)}
                onMouseEnter={() => setHoveredCard(`type-${option.type}`)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5
                           rounded-2xl transition-opacity duration-300`}
                />

                <div className="relative p-8 text-center">
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 rounded-full
                             bg-gradient-to-br ${gradient} text-white mb-4
                             shadow-lg group-hover:shadow-xl transition-all duration-300
                             group-hover:scale-110`}
                  >
                    {icon}
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-gray-800">
                    {t(option.titleKey)}
                  </h3>

                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                    {t(option.descKey)}
                  </p>

                  <div className="flex items-center justify-center">
                    <div className="flex items-center text-gray-500 group-hover:text-gray-700 transition-colors duration-300">
                      <span className="text-sm font-medium mr-2">
                        {t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.START')}
                      </span>
                      <ChevronRight className="w-5 h-5 transition-all duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // -------- ÉTAPE 2 : plateforme (admin) --------
  if (step === 'plateforme' && type !== null) {
    const selectedOption = typeOptions.find(o => o.type === type)!;
    const { icon, gradient } = getTypeVisual(type);

    return (
      <div className="max-w-4xl mx-auto p-10">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full
                         bg-gradient-to-br ${gradient} text-white mb-4 shadow-lg`}>
            {icon}
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t(selectedOption.titleKey)}
          </h2>
          <p className="text-gray-600">
            {t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.ORGANIZATION_STEP_DESCRIPTION')}
          </p>
        </div>

        <div className="max-w-md mx-auto mb-8">
          <Dropdown
            name="plateforme"
            label={t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.ORGANIZATION_LABEL')}
            required
            items={plateformeItems}
            selectedItem={plateformeItems.find(o => o.key === plateformeId) || null}
            onSelect={item => setPlateformeId(String(item.key))}
            placeholder={t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.ORGANIZATION_PLACEHOLDER')}
          />
        </div>

        {error && (
          <div className="max-w-md mx-auto mb-6 text-center text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="flex justify-center gap-4">
          <button
            type="button"
            onClick={handleBackToTypes}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
          >
            {t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.BACK_TO_TYPES')}
          </button>
          <button
            type="button"
            onClick={handleCreateForPlateforme}
            disabled={plateformeId === '' || isSubmitting}
            className="px-6 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.CREATE')}
          </button>
        </div>
      </div>
    );
  }

  // -------- ÉTAPE création en cours --------
  return (
    <div className="max-w-4xl mx-auto p-10">
      <div className="flex justify-center my-10">
        <Loading loading />
      </div>
      <p className="text-center text-gray-600">
        {t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.CREATING')}
      </p>
    </div>
  );
};
